import { useState } from "react";
import { CalendarDays, Hash, TextCursorInput } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import NumericBoxPlot from "@/frontend/features/Fde/data/NumericBoxPlot";
import type { ForgeFieldProfile } from "@/frontend/features/Fde/data/types";

type Props = {
  profiles: ForgeFieldProfile[];
  sourceRowCount?: number | null;
  sourceLabel?: string;
  copy: Record<string, string>;
};

type ProfileView = "distribution" | "summary";

const icons = {
  number: Hash,
  date: CalendarDays,
  text: TextCursorInput,
};

function shortValue(value: string) {
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  return value.length > 18 ? `${value.slice(0, 17)}…` : value;
}

function numericValue(value: string | number | null | undefined) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1, notation: Math.abs(Number(value)) >= 10_000 ? "compact" : "standard" }).format(Number(value));
}

function preciseNumericValue(value: string | number | null | undefined) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const numeric = Number(value);
  const precision = Math.abs(numeric) > 0 && Math.abs(numeric) < 1 ? 4 : 2;
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: precision }).format(numeric);
}

export default function DataProfileStrip({ profiles, sourceRowCount, sourceLabel, copy }: Props) {
  const [view, setView] = useState<ProfileView>(() => {
    if (typeof window === "undefined") return "distribution";
    return window.localStorage.getItem("wfn:data-profile-view") === "summary" ? "summary" : "distribution";
  });

  const selectView = (nextView: ProfileView) => {
    setView(nextView);
    window.localStorage.setItem("wfn:data-profile-view", nextView);
  };

  return (
    <section className="shrink-0 border-b border-border bg-muted/8 px-3 pb-2 pt-1.5" aria-label={copy.data_profile}>
      <header className="mb-1.5 flex h-7 items-center gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{copy.data_profile}</span>
          {sourceRowCount != null && (
            <span className="ml-2 text-[10px] tabular-nums text-emerald-700">
              <span className="mr-1 inline-block size-1.5 rounded-full bg-emerald-500" />
              {sourceLabel || copy.full_source} · {sourceRowCount.toLocaleString("pt-BR")} {copy.records}
            </span>
          )}
        </div>
        <div className="ml-auto flex rounded-md border border-border bg-background p-0.5" aria-label={copy.profile_view}>
          {(["distribution", "summary"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => selectView(option)}
              className={cn("h-6 rounded px-2 text-[10px] font-medium transition-colors", view === option ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              {option === "distribution" ? copy.profile_distribution : copy.profile_summary}
            </button>
          ))}
        </div>
      </header>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {profiles.map((profile) => {
          const Icon = icons[profile.kind];
          return (
            <article key={profile.name} className="w-56 shrink-0 rounded-lg border border-border/80 bg-background px-3 py-2 shadow-xs">
              <div className="flex items-center gap-1.5">
                <Icon className="size-3 text-primary" />
                <p className="min-w-0 flex-1 truncate text-[10px] font-semibold" title={profile.name}>{profile.label || profile.name}</p>
              </div>
              {view === "distribution" ? <Distribution profile={profile} copy={copy} /> : <Summary profile={profile} sourceRowCount={sourceRowCount} copy={copy} />}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Distribution({ profile, copy }: { profile: ForgeFieldProfile; copy: Record<string, string> }) {
  const maximum = Math.max(...profile.bars, 1);
  const hasDateRange = profile.kind === "date" && profile.minimum != null && profile.maximum != null;
  return (
    <>
      {profile.bars.length > 0 ? (
        <svg className="mt-2 h-8 w-full text-primary/55" viewBox="0 0 120 32" preserveAspectRatio="none" aria-hidden="true">
          {profile.bars.map((value, index) => {
            const width = 120 / profile.bars.length;
            const height = Math.max(4, (value / maximum) * 32);
            return <rect key={index} x={index * width + 0.75} y={32 - height} width={Math.max(1, width - 1.5)} height={height} rx="1.5" fill="currentColor" />;
          })}
        </svg>
      ) : hasDateRange ? (
        <p className="mt-2 h-8 text-[10px] leading-8 text-muted-foreground">{copy.available_period}</p>
      ) : (
        <p className="mt-2 h-8 truncate text-[10px] leading-8 text-muted-foreground" title={profile.top_values.map(String).join(" · ")}>
          {profile.top_values.slice(0, 3).map((value) => shortValue(String(value))).join(" · ") || copy.no_values}
        </p>
      )}
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[9px] tabular-nums text-muted-foreground">
        {hasDateRange ? (
          <><span>{shortValue(String(profile.minimum))}</span><span>{shortValue(String(profile.maximum))}</span></>
        ) : (
          <><span>{profile.distinct.toLocaleString("pt-BR")} {copy.distinct_values}</span><span>{profile.missing.toLocaleString("pt-BR")} {copy.missing_values}</span></>
        )}
      </div>
    </>
  );
}

function Summary({ profile, sourceRowCount, copy }: { profile: ForgeFieldProfile; sourceRowCount?: number | null; copy: Record<string, string> }) {
  // A field with no values at all shows that ("Sem valores", N vazios) instead of a row of dashes.
  if (profile.kind !== "number" || (profile.minimum == null && profile.maximum == null)) {
    return <Distribution profile={profile} copy={copy} />;
  }
  const boxValues = [profile.minimum, profile.maximum, profile.whisker_min, profile.first_quartile, profile.median, profile.third_quartile, profile.whisker_max];
  const hasBoxPlot = boxValues.every((value) => value != null && Number.isFinite(Number(value)));
  const minimum = Number(profile.minimum);
  const maximum = Number(profile.maximum);
  const whiskerMin = Number(profile.whisker_min);
  const firstQuartile = Number(profile.first_quartile);
  const median = Number(profile.median);
  const thirdQuartile = Number(profile.third_quartile);
  const whiskerMax = Number(profile.whisker_max);
  const statistics = [
    { label: copy.minimum, value: profile.minimum, help: copy.minimum_help },
    { label: copy.median, value: profile.median, help: copy.median_help },
    { label: copy.mean, value: profile.mean, help: copy.mean_help },
    { label: copy.maximum, value: profile.maximum, help: copy.maximum_help },
  ];
  return (
    <>
      {hasBoxPlot && (
        <NumericBoxPlot
          minimum={minimum}
          maximum={maximum}
          whiskerMin={whiskerMin}
          firstQuartile={firstQuartile}
          median={median}
          thirdQuartile={thirdQuartile}
          whiskerMax={whiskerMax}
          mean={profile.mean ?? null}
          standardDeviation={profile.standard_deviation ?? null}
          validCount={sourceRowCount == null ? null : Math.max(0, sourceRowCount - profile.missing)}
          copy={copy}
        />
      )}
      <dl className="mt-1 grid grid-cols-4 gap-1 text-[9px] tabular-nums">
        {statistics.map(({ label, value, help }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <div className="min-w-0 cursor-help rounded px-0.5 py-0.5 hover:bg-muted/60" tabIndex={0}>
                <dt className="truncate text-muted-foreground">{label}</dt>
                <dd className="truncate font-medium text-foreground">{numericValue(value)}</dd>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={5} className="max-w-56">
              <p className="font-medium">{label}: {preciseNumericValue(value)}</p>
              <p className="mt-0.5 text-muted-foreground">{help}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </dl>
    </>
  );
}
